import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UserRole = 'Administrator' | 'Designer' | 'Homeowner';

export type DesignerTier = 'Home' | 'Design' | 'Design+';
export type HomeownerTier = 'Basic' | 'Guild' | 'Guild+';
export type MembershipTier = DesignerTier | HomeownerTier | null;

interface ImpersonationState {
  isImpersonating: boolean;
  actualRole: UserRole | null;
  impersonatedRole: UserRole | null;
  impersonatedTier: MembershipTier;
  actualUserName: string | null;
}

interface ImpersonationContextType extends ImpersonationState {
  startImpersonation: (role: UserRole, tier?: MembershipTier) => void;
  stopImpersonation: () => void;
  getEffectiveRole: () => UserRole | null;
  getEffectiveTier: () => MembershipTier;
  canAccessAdminFeatures: () => boolean;
  canAccessFeature: (feature: string) => boolean;
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined);

const IMPERSONATION_STORAGE_KEY = 'hdg_impersonation_state';

interface ImpersonationProviderProps {
  children: ReactNode;
  actualRole: UserRole | null;
  actualUserName: string | null;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({
  children,
  actualRole,
  actualUserName,
}) => {
  const [state, setState] = useState<ImpersonationState>(() => {
    // Try to restore impersonation state from sessionStorage
    const stored = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Validate that the stored state matches current user
        if (parsed.actualRole === actualRole && parsed.actualUserName === actualUserName) {
          return parsed;
        }
      } catch (e) {
      }
    }
    
    return {
      isImpersonating: false,
      actualRole,
      impersonatedRole: null,
      impersonatedTier: null,
      actualUserName,
    };
  });

  // Update state when actual role/user changes
  useEffect(() => {
    setState((prev) => ({
      ...prev,
      actualRole,
      actualUserName,
    }));
  }, [actualRole, actualUserName]);

  // Persist impersonation state to sessionStorage
  useEffect(() => {
    if (state.isImpersonating) {
      sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  }, [state]);

  const startImpersonation = (role: UserRole, tier?: MembershipTier) => {
    // Only admins can impersonate
    if (actualRole !== 'Administrator') {
      return;
    }

    // Cannot impersonate your own role
    if (role === actualRole) {
      return;
    }

    const tierInfo = tier ? ` (${tier})` : '';
    
    // Log impersonation event
    logImpersonationEvent('start', role, tier);

    const newState = {
      isImpersonating: true,
      actualRole,
      impersonatedRole: role,
      impersonatedTier: tier || null,
      actualUserName,
    };
    
    setState(newState);
  };

  const stopImpersonation = () => {
    if (!state.isImpersonating) return;

    
    // Log impersonation event
    logImpersonationEvent('stop', state.impersonatedRole, state.impersonatedTier);

    setState({
      isImpersonating: false,
      actualRole,
      impersonatedRole: null,
      impersonatedTier: null,
      actualUserName,
    });
  };

  const getEffectiveRole = (): UserRole | null => {
    return state.isImpersonating ? state.impersonatedRole : state.actualRole;
  };

  const getEffectiveTier = (): MembershipTier => {
    return state.isImpersonating ? state.impersonatedTier : null;
  };

  const canAccessAdminFeatures = (): boolean => {
    // Admin features are only accessible by actual admins, not impersonated roles
    return state.actualRole === 'Administrator';
  };

  // Feature access control based on role and tier
  const canAccessFeature = (feature: string): boolean => {
    const role = getEffectiveRole();
    const tier = getEffectiveTier();

    if (role === 'Administrator') return true;

    if (role === 'Designer') {
      const designerFeatures = {
        'hdg-seal': ['Home', 'Design', 'Design+'],
        'regional-marketing': ['Home', 'Design', 'Design+'],
        'smart-matching': ['Home', 'Design', 'Design+'],
        'matching-dashboard': ['Home', 'Design', 'Design+'],
        'designer-file-profit': ['Design', 'Design+'],
        'homeowner-financing': ['Design+'],
        'designer-referral': ['Design+'],
      };

      const allowedTiers = designerFeatures[feature as keyof typeof designerFeatures] || [];
      return tier ? allowedTiers.includes(tier as string) : false;
    }

    if (role === 'Homeowner') {
      const homeownerFeatures = {
        'limited-gallery': ['Basic', 'Guild', 'Guild+'],
        'limited-articles': ['Basic', 'Guild', 'Guild+'],
        'curated-gallery': ['Guild', 'Guild+'],
        'guild-articles': ['Guild', 'Guild+'],
        'powerful-filters': ['Guild', 'Guild+'],
        'idea-books': ['Guild', 'Guild+'],
        'smart-guild-matching': ['Guild', 'Guild+'],
        'education': ['Guild', 'Guild+'],
      };

      const allowedTiers = homeownerFeatures[feature as keyof typeof homeownerFeatures] || [];
      return tier ? allowedTiers.includes(tier as string) : false;
    }

    return false;
  };

  const logImpersonationEvent = (action: 'start' | 'stop', role: UserRole | null, tier?: MembershipTier) => {
    // Log to console for now - in production, this would send to an audit log
    const event = {
      timestamp: new Date().toISOString(),
      action,
      adminUser: actualUserName,
      adminRole: actualRole,
      impersonatedRole: role,
      impersonatedTier: tier,
      userAgent: navigator.userAgent,
    };


    // Store in localStorage for audit trail (in production, send to backend)
    try {
      const auditLog = JSON.parse(localStorage.getItem('hdg_impersonation_audit') || '[]');
      auditLog.push(event);
      // Keep only last 100 events
      if (auditLog.length > 100) {
        auditLog.shift();
      }
      localStorage.setItem('hdg_impersonation_audit', JSON.stringify(auditLog));
    } catch (e) {
    }
  };

  // Clear impersonation on component unmount (logout)
  useEffect(() => {
    return () => {
      if (state.isImpersonating) {
        sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
      }
    };
  }, [state.isImpersonating]);

  // Debug: Log state changes
  useEffect(() => {
  }, [state]);

  return (
    <ImpersonationContext.Provider
      value={{
        ...state,
        startImpersonation,
        stopImpersonation,
        getEffectiveRole,
        getEffectiveTier,
        canAccessAdminFeatures,
        canAccessFeature,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (!context) {
    throw new Error('useImpersonation must be used within ImpersonationProvider');
  }
  return context;
};
